// Mock data fixtures for the merchant portal.
// Realistic, narratively coherent — the merchant is "Athena Threadworks",
// an Estonia-registered apparel + accessories brand serving consumers
// across the EU + Balkans.

export const merchant = {
  name: "Athena Threadworks",
  id: "MERCH-029-EU",
  status: "Live",
  reportingCurrency: "EUR",
  defaultIban: "EE38 7700 7710 0000 0417",
  region: "Estonia · EU",
};

export const user = {
  name: "Eleni Mikkelsen",
  email: "eleni@athena-threadworks.com",
  role: "Admin · Treasury",
  initials: "EM",
};

// ─────────────────────────────────────────────────────────────
// THE THREE BALANCES
// ─────────────────────────────────────────────────────────────
export const balances = {
  payIn: {
    label: "Pay-In Balance",
    note: "Customer funds collected · awaiting settlement",
    amount: 1_847_290.04,
    currency: "EUR",
    deltaPct: 18.2,
    deltaLabel: "vs. previous 7d",
    foot: "Updated 09:14 CET",
    tag: "Customer Funds",
  },
  payOut: {
    label: "Pay-Out Balance",
    note: "Reserved for outbound to customers",
    amount: 84_210.5,
    currency: "EUR",
    deltaPct: -12.4,
    deltaLabel: "3-day runway",
    foot: "Avg. daily out €28,070",
    tag: "Liquidity",
  },
  operational: {
    label: "Operational Balance",
    note: "Working capital · business account",
    amount: 482_015.2,
    currency: "EUR",
    deltaPct: 5.8,
    deltaLabel: "after settlement on 03 May",
    foot: "Acc. EE38…417 · Default",
    tag: "Treasury",
  },
};

// ─────────────────────────────────────────────────────────────
// CHART DATA — 90 days of synthetic but realistic *flow* series.
//
// Pay-In and Pay-Out are daily flows (money moved that day). They share
// a Y axis and tell one story: cash in vs cash out. Operational balance
// is a stock (point-in-time balance) and lives in the triptych above —
// it does not belong on the same axis as flows.
//
// uPlot expects [timestamps[], series1[], series2[]…]
// ─────────────────────────────────────────────────────────────
function buildSeries(days: number) {
  const now = Math.floor(Date.now() / 1000);
  const timestamps: number[] = [];
  const payIn: number[] = [];
  const payOut: number[] = [];

  // Deterministic seeded RNG so the chart is identical across runs.
  let seed = 9001;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  // Realistic merchant retail cycle:
  // - peak Thu/Fri/Sat, dip Sun/Mon
  // - smooth upward trend over the window
  // - independent jitter on pay-out so the two lines aren't perfect mirrors
  const dayWeight = [0.7, 0.78, 0.95, 1.05, 1.18, 1.22, 0.92]; // Sun..Sat

  for (let i = days - 1; i >= 0; i--) {
    const ts = now - i * 86400;
    timestamps.push(ts);

    const day = new Date(ts * 1000).getDay();
    const cycle = dayWeight[day] ?? 1;

    // Trend baseline: 140k → 290k over the window, eased.
    const t = (days - 1 - i) / Math.max(1, days - 1);
    const eased = 0.5 - Math.cos(t * Math.PI) * 0.5;
    const inBase = 140_000 + eased * 150_000;
    const inJitter = (rand() - 0.5) * 24_000;
    const inVal = Math.round(inBase * cycle + inJitter);
    payIn.push(Math.max(40_000, inVal));

    // Pay-out tracks roughly 60–68% of pay-in, with its own jitter and a
    // slight phase shift so it doesn't look like a parallel echo.
    const outRatio = 0.62 + Math.sin(i * 0.18) * 0.04;
    const outJitter = (rand() - 0.5) * 18_000;
    const outVal = Math.round(inVal * outRatio + outJitter);
    payOut.push(Math.max(25_000, outVal));
  }

  return { timestamps, payIn, payOut };
}

export const chartSeries = buildSeries(90);

// ─────────────────────────────────────────────────────────────
// METHOD MIX — current 7d
// ─────────────────────────────────────────────────────────────
export const methodMix = [
  { key: "wallet", label: "Wallet", pct: 42, value: 775_861 },
  { key: "card", label: "Card / Gateway", pct: 28, value: 517_241 },
  { key: "crypto", label: "Crypto", pct: 18, value: 332_512 },
  { key: "voucher", label: "Voucher", pct: 12, value: 221_675 },
] as const;

// ─────────────────────────────────────────────────────────────
// PAY-INS (live tape) — used by Dashboard preview AND Pay-Ins page
// ─────────────────────────────────────────────────────────────
export type Method = "wallet" | "card" | "crypto" | "voucher" | "sepa";
export type StatusKind = "success" | "pending" | "failed";

export interface Transaction {
  id: string;
  ref: string;
  date: string;
  time: string;
  customer: string;
  description: string;
  method: Method;
  methodLabel: string;
  amountEur: number;
  amountText?: string;
  direction: "in" | "out";
  status: StatusKind;
  statusLabel: string;
}

export const payIns: Transaction[] = [
  {
    id: "TX-892014",
    ref: "ATH-WAL-29",
    date: "06 May",
    time: "09:12 CET",
    customer: "Niamh O'Sullivan",
    description: "Wallet top-up",
    method: "wallet",
    methodLabel: "Wallet",
    amountEur: 2418,
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
  {
    id: "TX-892008",
    ref: "USDC → Wallet",
    date: "06 May",
    time: "08:34 CET",
    customer: "Anya Petrović",
    description: "Crypto top-up · USDC",
    method: "crypto",
    methodLabel: "USDC · Solana",
    amountEur: 940,
    amountText: "USDC 1,008.40",
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
  {
    id: "TX-891998",
    ref: "MAY-RUNNER",
    date: "06 May",
    time: "08:21 CET",
    customer: 'Voucher · "MAY-RUNNER"',
    description: "Marketplace promotion",
    method: "voucher",
    methodLabel: "Voucher",
    amountEur: 55,
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
  {
    id: "TX-891982",
    ref: "ATH-WAL-29",
    date: "06 May",
    time: "07:58 CET",
    customer: "Maël Lefèvre",
    description: "Wallet top-up",
    method: "wallet",
    methodLabel: "Wallet",
    amountEur: 420,
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
  {
    id: "TX-891940",
    ref: "ATH-CARD-12",
    date: "06 May",
    time: "07:14 CET",
    customer: "Sergi Vilanova",
    description: "Card · Visa ••2014",
    method: "card",
    methodLabel: "Card · Gateway",
    amountEur: 188,
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
  {
    id: "TX-891891",
    ref: "ATH-WAL-29",
    date: "05 May",
    time: "23:04 CET",
    customer: "Lasse Ahlberg",
    description: "Wallet top-up",
    method: "wallet",
    methodLabel: "Wallet",
    amountEur: 95,
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
  {
    id: "TX-891854",
    ref: "ATH-CARD-12",
    date: "05 May",
    time: "21:48 CET",
    customer: "Aleksandra Novak",
    description: "Card · Mastercard ••0142",
    method: "card",
    methodLabel: "Card · Gateway",
    amountEur: 312,
    direction: "in",
    status: "pending",
    statusLabel: "Pending",
  },
  {
    id: "TX-891832",
    ref: "USDC → Wallet",
    date: "05 May",
    time: "20:26 CET",
    customer: "Yannis Kostaras",
    description: "Crypto top-up · USDC",
    method: "crypto",
    methodLabel: "USDC · Solana",
    amountEur: 2105,
    amountText: "USDC 2,253.05",
    direction: "in",
    status: "success",
    statusLabel: "Settled",
  },
];

// ─────────────────────────────────────────────────────────────
// PAY-OUTS — outbound to customers
// ─────────────────────────────────────────────────────────────
export const payOuts: Transaction[] = [
  {
    id: "PO-A-1108",
    ref: "Maybank Card ••4419",
    date: "06 May",
    time: "09:08 CET",
    customer: "Karim Asfour",
    description: "Refund · order #82014",
    method: "card",
    methodLabel: "Card · Gateway",
    amountEur: 1200,
    direction: "out",
    status: "pending",
    statusLabel: "Pending",
  },
  {
    id: "PO-A-1107",
    ref: "Wise · acc. ••4017",
    date: "06 May",
    time: "08:42 CET",
    customer: "Petra Lindqvist",
    description: "Customer pay-out",
    method: "sepa",
    methodLabel: "SEPA",
    amountEur: 480,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "PO-A-1105",
    ref: "Revolut · acc. ••8821",
    date: "06 May",
    time: "07:55 CET",
    customer: "Marko Đurović",
    description: "Customer pay-out",
    method: "sepa",
    methodLabel: "SEPA",
    amountEur: 2200,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "PO-A-1101",
    ref: "USDC · 0xA1f4…29Bd",
    date: "05 May",
    time: "22:18 CET",
    customer: "Janka Tavárez",
    description: "Customer pay-out · crypto",
    method: "crypto",
    methodLabel: "USDC · Solana",
    amountEur: 850,
    amountText: "USDC 909.50",
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "PO-A-1098",
    ref: "Card ••3315",
    date: "05 May",
    time: "19:02 CET",
    customer: "Mikołaj Dąbrowski",
    description: "Refund · order #82008",
    method: "card",
    methodLabel: "Card · Gateway",
    amountEur: 320,
    direction: "out",
    status: "failed",
    statusLabel: "Failed",
  },
  {
    id: "PO-A-1095",
    ref: "Maybank · acc. ••2240",
    date: "05 May",
    time: "16:14 CET",
    customer: "Henrik Solberg",
    description: "Customer pay-out",
    method: "sepa",
    methodLabel: "SEPA",
    amountEur: 1750,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
];

// ─────────────────────────────────────────────────────────────
// SETTLEMENTS HISTORY
// ─────────────────────────────────────────────────────────────
export const settlements = [
  {
    id: "STL-2026-018",
    date: "03 May 2026",
    amount: 481_200.0,
    destination: "Acc. EE38…417",
    status: "success" as StatusKind,
    statusLabel: "Cleared",
  },
  {
    id: "STL-2026-017",
    date: "26 Apr 2026",
    amount: 312_840.0,
    destination: "Acc. EE38…417",
    status: "success" as StatusKind,
    statusLabel: "Cleared",
  },
  {
    id: "STL-2026-016",
    date: "19 Apr 2026",
    amount: 408_510.0,
    destination: "Acc. EE38…417",
    status: "success" as StatusKind,
    statusLabel: "Cleared",
  },
  {
    id: "STL-2026-015",
    date: "12 Apr 2026",
    amount: 222_310.0,
    destination: "Acc. EE38…417",
    status: "success" as StatusKind,
    statusLabel: "Cleared",
  },
];

export const settlementSnapshot = {
  available: 553_172.4,
  weeklyCap: 725_000,
  pendingHold: 61_840,
  lastSettled: settlements[0],
};

// ─────────────────────────────────────────────────────────────
// CRYPTO — assets + activity + wallet
// ─────────────────────────────────────────────────────────────
export const cryptoAssets = [
  {
    symbol: "USDC",
    name: "USD Coin",
    network: "Solana",
    address: "0xA1f4…29Bd",
    balance: 52_418.2,
    eurEquivalent: 48_940,
    deltaPct: 0.4,
  },
  {
    symbol: "EURC",
    name: "Euro Coin",
    network: "Ethereum",
    address: "0x9c2…BA10",
    balance: 27_350.0,
    eurEquivalent: 27_350,
    deltaPct: 0,
  },
];

// ─────────────────────────────────────────────────────────────
// PROMOTIONS
// ─────────────────────────────────────────────────────────────
export type PromoStatus =
  | "draft"
  | "submitted"
  | "review"
  | "approved"
  | "live"
  | "rejected";

export interface Promotion {
  id: string;
  title: string;
  reward: string;
  status: PromoStatus;
  statusLabel: string;
  audience: string;
  budget: number;
  budgetUsed: number;
  startDate?: string;
  endDate?: string;
  redemptions?: number;
  uplift?: number;
}

export const promotions: Promotion[] = [
  {
    id: "P-2026-014",
    title: "May Spring Runner",
    reward: "5% wallet credit",
    status: "live",
    statusLabel: "Live in marketplace",
    audience: "Wallet users · all regions",
    budget: 10_000,
    budgetUsed: 6200,
    startDate: "02 May",
    endDate: "19 May",
    redemptions: 1294,
    uplift: 24,
  },
  {
    id: "P-2026-015",
    title: "Crypto Settler",
    reward: "€5 bonus on first USDC top-up",
    status: "live",
    statusLabel: "Live in marketplace",
    audience: "New crypto users · EU",
    budget: 4000,
    budgetUsed: 1920,
    startDate: "01 May",
    endDate: "31 May",
    redemptions: 384,
    uplift: 12,
  },
  {
    id: "P-2026-016",
    title: "Mid-Spring Loyalty",
    reward: "Free shipping over €60",
    status: "approved",
    statusLabel: "Approved",
    audience: "Returning customers · 90d",
    budget: 6000,
    budgetUsed: 0,
    startDate: "10 May",
    endDate: "10 Jun",
    redemptions: 0,
    uplift: 0,
  },
  {
    id: "P-2026-017",
    title: "First-Order Voucher",
    reward: "10% off first order",
    status: "review",
    statusLabel: "Under review",
    audience: "New customers · all regions",
    budget: 8000,
    budgetUsed: 0,
  },
  {
    id: "P-2026-018",
    title: "Summer Pre-Launch",
    reward: "€10 wallet credit on €100+ spend",
    status: "submitted",
    statusLabel: "Submitted",
    audience: "Wallet users · EU",
    budget: 12_000,
    budgetUsed: 0,
  },
  {
    id: "P-2026-019",
    title: "Athletes Friend",
    reward: "Buy-one-get-one running tee",
    status: "draft",
    statusLabel: "Draft",
    audience: "TBD",
    budget: 0,
    budgetUsed: 0,
  },
  {
    id: "P-2026-013",
    title: "April Voucher Push",
    reward: "€5 marketplace voucher",
    status: "rejected",
    statusLabel: "Rejected",
    audience: "All users · EU",
    budget: 5000,
    budgetUsed: 0,
  },
];

// ─────────────────────────────────────────────────────────────
// BUSINESS ACCOUNT — operational treasury
// ─────────────────────────────────────────────────────────────
export const businessAccount = {
  iban: "EE38 7700 7710 0000 0417",
  bic: "LHVBEE22XXX",
  bank: "LHV Pank · Tallinn",
  accountName: "Athena Threadworks OÜ",
  available: 482_015.2,
  pendingOutgoing: 27_840,
  monthlyOutflow: 268_120,
};

export interface BusinessPayment {
  id: string;
  date: string;
  time: string;
  beneficiary: string;
  reference: string;
  iban?: string;
  swiftBic?: string;
  rail: "sepa" | "swift" | "card" | "internal";
  railLabel: string;
  amountEur: number;
  amountText?: string;
  direction: "in" | "out";
  status: StatusKind;
  statusLabel: string;
}

export const businessPayments: BusinessPayment[] = [
  {
    id: "BP-2026-0481",
    date: "06 May",
    time: "08:51 CET",
    beneficiary: "Vendor 081 · Wholesale fabric",
    reference: "INV-2026-0481",
    iban: "EE38 7700 4220 0091 0712",
    rail: "sepa",
    railLabel: "SEPA",
    amountEur: 18_500,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "BP-2026-0480",
    date: "05 May",
    time: "16:22 CET",
    beneficiary: "Studio Lab Athens · Photography",
    reference: "PO-2026-228",
    iban: "GR16 0110 1250 0000 0001 2300 695",
    rail: "sepa",
    railLabel: "SEPA",
    amountEur: 4250,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "BP-2026-0479",
    date: "05 May",
    time: "11:07 CET",
    beneficiary: "BeyondGarments Ltd · UK supplier",
    reference: "PO-2026-227 · GBP 12,400",
    swiftBic: "BARCGB22XXX",
    rail: "swift",
    railLabel: "SWIFT",
    amountEur: 14_682,
    amountText: "GBP 12,400",
    direction: "out",
    status: "pending",
    statusLabel: "Pending",
  },
  {
    id: "BP-2026-0478",
    date: "04 May",
    time: "18:44 CET",
    beneficiary: "Operational ↔ Pay-Out top-up",
    reference: "Internal · liquidity refill",
    rail: "internal",
    railLabel: "Internal",
    amountEur: 140_000,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "BP-2026-0477",
    date: "04 May",
    time: "10:12 CET",
    beneficiary: "Klaviyo Inc · Email tooling",
    reference: "Card · ••2412 · subscription",
    rail: "card",
    railLabel: "Card",
    amountEur: 348,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "BP-2026-0476",
    date: "03 May",
    time: "14:38 CET",
    beneficiary: "Customer Funds → Operational",
    reference: "STL-2026-018 · settlement",
    rail: "internal",
    railLabel: "Settlement",
    amountEur: 481_200,
    direction: "in",
    status: "success",
    statusLabel: "Cleared",
  },
  {
    id: "BP-2026-0475",
    date: "02 May",
    time: "09:55 CET",
    beneficiary: "Marketing Agency Sofia",
    reference: "INV-MK-2026-018",
    iban: "BG18 RZBB 9155 4801 1280 27",
    rail: "sepa",
    railLabel: "SEPA",
    amountEur: 6800,
    direction: "out",
    status: "success",
    statusLabel: "Cleared",
  },
];

// ─────────────────────────────────────────────────────────────
// VIRTUAL CARDS — for ad spend, subscriptions, vendor cards
// ─────────────────────────────────────────────────────────────
export interface VirtualCard {
  id: string;
  last4: string;
  label: string;
  holder: string;
  status: "active" | "frozen" | "expired";
  statusLabel: string;
  spent30d: number;
  limit: number;
  cycle: "monthly" | "weekly" | "one-off";
  cycleLabel: string;
  expiry: string;
  brand: "visa" | "mastercard";
  category: string;
}

export const virtualCards: VirtualCard[] = [
  {
    id: "VC-001",
    last4: "2412",
    label: "Marketing · Klaviyo + Mailchimp",
    holder: "Eleni Mikkelsen",
    status: "active",
    statusLabel: "Active",
    spent30d: 1284,
    limit: 2500,
    cycle: "monthly",
    cycleLabel: "Monthly cap",
    expiry: "08 / 28",
    brand: "visa",
    category: "Software & subscriptions",
  },
  {
    id: "VC-002",
    last4: "8870",
    label: "Meta Ads · EU campaigns",
    holder: "Aida Rusu",
    status: "active",
    statusLabel: "Active",
    spent30d: 8420,
    limit: 12_000,
    cycle: "monthly",
    cycleLabel: "Monthly cap",
    expiry: "11 / 27",
    brand: "mastercard",
    category: "Advertising",
  },
  {
    id: "VC-003",
    last4: "4419",
    label: "Travel · Berlin showcase",
    holder: "Dimitrios Halkias",
    status: "active",
    statusLabel: "Active",
    spent30d: 2148,
    limit: 5000,
    cycle: "one-off",
    cycleLabel: "One-off cap",
    expiry: "06 / 26",
    brand: "visa",
    category: "Travel",
  },
  {
    id: "VC-004",
    last4: "0142",
    label: "Cloud · Cloudflare + Sanity",
    holder: "Joost Verbeek",
    status: "active",
    statusLabel: "Active",
    spent30d: 612,
    limit: 1500,
    cycle: "monthly",
    cycleLabel: "Monthly cap",
    expiry: "03 / 29",
    brand: "mastercard",
    category: "Software & infrastructure",
  },
  {
    id: "VC-005",
    last4: "3315",
    label: "Logistics · DHL deposits",
    holder: "Aida Rusu",
    status: "frozen",
    statusLabel: "Frozen",
    spent30d: 0,
    limit: 4000,
    cycle: "monthly",
    cycleLabel: "Monthly cap",
    expiry: "01 / 28",
    brand: "visa",
    category: "Logistics",
  },
];

export interface CardCharge {
  date: string;
  time: string;
  cardLast4: string;
  merchant: string;
  category: string;
  amountEur: number;
  status: StatusKind;
  statusLabel: string;
}

export const cardCharges: CardCharge[] = [
  {
    date: "06 May",
    time: "07:22 CET",
    cardLast4: "8870",
    merchant: "Meta Platforms · EU Ads",
    category: "Advertising",
    amountEur: 1240,
    status: "success",
    statusLabel: "Cleared",
  },
  {
    date: "05 May",
    time: "23:18 CET",
    cardLast4: "8870",
    merchant: "Meta Platforms · EU Ads",
    category: "Advertising",
    amountEur: 980,
    status: "success",
    statusLabel: "Cleared",
  },
  {
    date: "05 May",
    time: "14:08 CET",
    cardLast4: "0142",
    merchant: "Cloudflare, Inc.",
    category: "Infrastructure",
    amountEur: 89,
    status: "success",
    statusLabel: "Cleared",
  },
  {
    date: "04 May",
    time: "10:12 CET",
    cardLast4: "2412",
    merchant: "Klaviyo Inc.",
    category: "Software",
    amountEur: 348,
    status: "success",
    statusLabel: "Cleared",
  },
  {
    date: "03 May",
    time: "20:47 CET",
    cardLast4: "4419",
    merchant: "Lufthansa · LH-2018",
    category: "Travel",
    amountEur: 412,
    status: "success",
    statusLabel: "Cleared",
  },
  {
    date: "03 May",
    time: "08:50 CET",
    cardLast4: "0142",
    merchant: "Sanity Studio",
    category: "Software",
    amountEur: 99,
    status: "success",
    statusLabel: "Cleared",
  },
  {
    date: "02 May",
    time: "12:34 CET",
    cardLast4: "8870",
    merchant: "Google Ads",
    category: "Advertising",
    amountEur: 720,
    status: "pending",
    statusLabel: "Pending",
  },
];

// ─────────────────────────────────────────────────────────────
// BENEFICIARIES — saved external recipients
// ─────────────────────────────────────────────────────────────
export interface Beneficiary {
  id: string;
  name: string;
  rail: "sepa" | "swift";
  iban?: string;
  swiftBic?: string;
  country: string;
  category: string;
  lastPaid: string;
  totalPaidYtd: number;
}

export const beneficiaries: Beneficiary[] = [
  {
    id: "BEN-001",
    name: "Vendor 081 · Wholesale fabric",
    rail: "sepa",
    iban: "EE38 7700 4220 0091 0712",
    country: "Estonia",
    category: "Suppliers",
    lastPaid: "06 May",
    totalPaidYtd: 184_300,
  },
  {
    id: "BEN-002",
    name: "Marketing Agency Sofia",
    rail: "sepa",
    iban: "BG18 RZBB 9155 4801 1280 27",
    country: "Bulgaria",
    category: "Services",
    lastPaid: "02 May",
    totalPaidYtd: 41_200,
  },
  {
    id: "BEN-003",
    name: "BeyondGarments Ltd",
    rail: "swift",
    swiftBic: "BARCGB22XXX",
    country: "United Kingdom",
    category: "Suppliers",
    lastPaid: "05 May",
    totalPaidYtd: 92_400,
  },
  {
    id: "BEN-004",
    name: "Studio Lab Athens",
    rail: "sepa",
    iban: "GR16 0110 1250 0000 0001 2300 695",
    country: "Greece",
    category: "Services",
    lastPaid: "05 May",
    totalPaidYtd: 18_350,
  },
  {
    id: "BEN-005",
    name: "Logistics Partner Rotterdam",
    rail: "sepa",
    iban: "NL44 INGB 0007 1100 12",
    country: "Netherlands",
    category: "Logistics",
    lastPaid: "28 Apr",
    totalPaidYtd: 56_800,
  },
];

// ─────────────────────────────────────────────────────────────
// PAYROLL — bulk salary upload
// ─────────────────────────────────────────────────────────────
export type PayrollIssue = "duplicate" | "missing-iban" | "over-threshold";

export interface PayrollRow {
  rowNo: number;
  name: string;
  role: string;
  iban: string;
  amountEur: number;
  notes: string;
  issue?: PayrollIssue;
  issueText?: string;
}

export const payrollRows: PayrollRow[] = [
  {
    rowNo: 1,
    name: "Eleni Mikkelsen",
    role: "Treasury Lead",
    iban: "EE38 2200 2210 0084 1730",
    amountEur: 5400,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 2,
    name: "Dimitrios Halkias",
    role: "Founder",
    iban: "EE38 2200 2210 0094 4180",
    amountEur: 6200,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 3,
    name: "Aida Rusu",
    role: "Operations",
    iban: "RO49 AAAA 1B31 0075 9384 0000",
    amountEur: 3850,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 4,
    name: "Joost Verbeek",
    role: "Developer",
    iban: "NL91 ABNA 0417 1643 00",
    amountEur: 4900,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 5,
    name: "Rania Petrova",
    role: "Reports & Audit",
    iban: "BG18 RZBB 9155 4801 4080 73",
    amountEur: 3200,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 6,
    name: "Niamh O'Sullivan",
    role: "Studio · Atelier",
    iban: "IE29 AIBK 9311 5212 3456 78",
    amountEur: 3600,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 7,
    name: "Karim Asfour",
    role: "Customer Care",
    iban: "EE38 2200 2210 0091 8810",
    amountEur: 2900,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 8,
    name: "Sergi Vilanova",
    role: "Studio · Pattern",
    iban: "ES79 2100 0813 6101 2345 6789",
    amountEur: 3400,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 9,
    name: "Petra Lindqvist",
    role: "Brand",
    iban: "SE45 5000 0000 0583 9825 7466",
    amountEur: 4200,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 10,
    name: "Mikołaj Dąbrowski",
    role: "Logistics",
    iban: "PL61 1090 1014 0000 0712 1981 2874",
    amountEur: 3300,
    notes: "Salary · May 2026",
  },
  {
    rowNo: 11,
    name: "Henrik Solberg",
    role: "Wholesale",
    iban: "",
    amountEur: 4100,
    notes: "Salary · May 2026",
    issue: "missing-iban",
    issueText: "IBAN field is empty — fill or remove this row",
  },
  {
    rowNo: 12,
    name: "Tomáš Kovac",
    role: "Studio · Tech",
    iban: "SK31 1200 0000 1987 4263 7541",
    amountEur: 12_500,
    notes: "Bonus · Q1 distribution",
    issue: "over-threshold",
    issueText: "Above €10,000 single-line approval threshold — needs Owner sign-off",
  },
  {
    rowNo: 13,
    name: "Aida Rusu",
    role: "Operations",
    iban: "RO49 AAAA 1B31 0075 9384 0000",
    amountEur: 250,
    notes: "Reimbursement · travel",
    issue: "duplicate",
    issueText: "Same beneficiary as row 3 — usually one row per person per run",
  },
  {
    rowNo: 14,
    name: "Yannis Kostaras",
    role: "Studio · Sample",
    iban: "GR16 0110 1250 0000 0001 2400 802",
    amountEur: 3500,
    notes: "Salary · May 2026",
  },
];

export const payrollHistory = [
  {
    id: "PR-2026-04",
    runDate: "30 Apr 2026",
    headcount: 14,
    totalEur: 61_350,
    status: "success" as StatusKind,
    statusLabel: "Cleared",
    operator: "Eleni Mikkelsen",
  },
  {
    id: "PR-2026-03",
    runDate: "31 Mar 2026",
    headcount: 13,
    totalEur: 56_400,
    status: "success" as StatusKind,
    statusLabel: "Cleared",
    operator: "Eleni Mikkelsen",
  },
  {
    id: "PR-2026-02",
    runDate: "29 Feb 2026",
    headcount: 13,
    totalEur: 56_100,
    status: "success" as StatusKind,
    statusLabel: "Cleared",
    operator: "Dimitrios Halkias",
  },
];

// ─────────────────────────────────────────────────────────────
// TEAM
// ─────────────────────────────────────────────────────────────
export interface TeamMember {
  initials: string;
  name: string;
  email: string;
  role: "owner" | "admin" | "operator" | "developer" | "view";
  roleLabel: string;
  scope: string;
  lastActive: string;
  status: "active" | "invited" | "suspended";
}

export const team: TeamMember[] = [
  {
    initials: "EM",
    name: "Eleni Mikkelsen",
    email: "eleni@athena-threadworks.com",
    role: "admin",
    roleLabel: "Admin · Treasury",
    scope: "All · including settlements",
    lastActive: "Now",
    status: "active",
  },
  {
    initials: "DH",
    name: "Dimitrios Halkias",
    email: "dimitri@athena-threadworks.com",
    role: "owner",
    roleLabel: "Owner",
    scope: "All",
    lastActive: "2h ago",
    status: "active",
  },
  {
    initials: "AR",
    name: "Aida Rusu",
    email: "aida@athena-threadworks.com",
    role: "operator",
    roleLabel: "Operations",
    scope: "Pay-Outs · Customer Support",
    lastActive: "12m ago",
    status: "active",
  },
  {
    initials: "JV",
    name: "Joost Verbeek",
    email: "joost@athena-threadworks.com",
    role: "developer",
    roleLabel: "Developer",
    scope: "API · Webhooks · Sandbox",
    lastActive: "Yesterday",
    status: "active",
  },
  {
    initials: "RP",
    name: "Rania Petrova",
    email: "rania@athena-threadworks.com",
    role: "view",
    roleLabel: "View only",
    scope: "Reports · Statements",
    lastActive: "—",
    status: "invited",
  },
];

// ─────────────────────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────────────────────
export function fmtEur(n: number, opts?: { compact?: boolean }): string {
  if (opts?.compact && Math.abs(n) >= 1000) {
    if (Math.abs(n) >= 1_000_000) return `€${(n / 1_000_000).toFixed(2)}M`;
    return `€${(n / 1000).toFixed(0)}k`;
  }
  return new Intl.NumberFormat("en-EU", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function fmtNumber(n: number, dp = 2): string {
  return new Intl.NumberFormat("en-EU", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(n);
}

export function splitAmount(n: number): { whole: string; frac: string } {
  const fixed = Math.abs(n).toFixed(2);
  const [whole = "0", frac = "00"] = fixed.split(".");
  const sign = n < 0 ? "-" : "";
  return {
    whole: sign + new Intl.NumberFormat("en-EU").format(Number(whole)),
    frac: `.${frac}`,
  };
}
